import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateUnidadRequestSchema, type CreateUnidadRequest } from '@condo/contracts';
import { Button } from '@/components/ui/button';

interface UnidadFormProps {
  defaultValues?: Partial<CreateUnidadRequest>;
  submitting?: boolean;
  submitLabel: string;
  onSubmit: (values: CreateUnidadRequest) => void;
  /** Deshabilita el campo `tipo` (edit mode no permite cambiar tipo). */
  lockTipo?: boolean;
}

export function UnidadForm({
  defaultValues,
  submitting,
  submitLabel,
  onSubmit,
  lockTipo = false,
}: UnidadFormProps) {
  const form = useForm<
    import('zod').input<typeof CreateUnidadRequestSchema>,
    unknown,
    import('zod').output<typeof CreateUnidadRequestSchema>
  >({
    resolver: zodResolver(CreateUnidadRequestSchema),
    defaultValues: {
      numero: '',
      tipo: 'vivienda',
      m2: 0,
      alicuota: 0,
      ...defaultValues,
    },
  });

  return (
    <form
      onSubmit={form.handleSubmit((values) => onSubmit(values))}
      className="space-y-4"
    >
      <div>
        <label className="eyebrow">Número</label>
        <input
          className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
          {...form.register('numero')}
        />
        {form.formState.errors.numero && (
          <p className="mt-1 text-xs text-[var(--color-danger)]">
            {form.formState.errors.numero.message}
          </p>
        )}
      </div>
      <div>
        <label className="eyebrow">Tipo</label>
        <select
          className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
          {...form.register('tipo')}
          disabled={lockTipo}
        >
          <option value="vivienda">Vivienda</option>
          <option value="estacionamiento">Estacionamiento</option>
          <option value="bodega">Bodega</option>
          <option value="comercial">Comercial</option>
          <option value="oficina">Oficina</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="eyebrow">m²</label>
          <input
            type="number"
            step="0.01"
            className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
            {...form.register('m2', { valueAsNumber: true })}
          />
        </div>
        <div>
          <label className="eyebrow">Alícuota (fracción, 0–1)</label>
          <input
            type="number"
            step="0.0001"
            className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
            {...form.register('alicuota', { valueAsNumber: true })}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="eyebrow">Dormitorios (opcional)</label>
          <input
            type="number"
            className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
            {...form.register('dormitorios', {
              setValueAs: (v) =>
                v === '' || v === null || Number.isNaN(Number(v))
                  ? undefined
                  : Number(v),
            })}
          />
        </div>
        <div>
          <label className="eyebrow">Piso (opcional)</label>
          <input
            type="number"
            className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
            {...form.register('piso', {
              setValueAs: (v) =>
                v === '' || v === null || Number.isNaN(Number(v))
                  ? undefined
                  : Number(v),
            })}
          />
        </div>
      </div>
      <Button type="submit" disabled={submitting}>
        {submitting ? 'Guardando…' : submitLabel}
      </Button>
    </form>
  );
}
